import { useMemo } from "react";

import { RIG_FRAC_LAYER_TYPE } from "constants/mapLayers.constants";
import { useMapStore } from "stores/map";

import useBetaFeatures from "hooks/useBetaFeatures";

import { MapLayerSettings } from "components/project/layers/components/layer-managers/shared/map-layer-settings/MapLayerSettings";
import {
  RigFracDetailsButton,
  RigFracFilterButton
} from "components/project/layers/components/rig-frac-details";
import { useProjectContext } from "components/project/projects/context";

import { useProjectLayerListQuery } from "../../../../../../queries";
import {
  FlexItem,
  LayerActionsWrapper,
  NodeTitle
} from "../../../../shared/nodes/StyledNodePartials";

export interface IRigFracNode {
  node;
}

/**
 * Tree node for the Rigs / Frac Crews layers. The tree node only carries the
 * layer name as its key, so the persisted layer id is resolved from the layer
 * list to feed the Color By drill-in and its mutations.
 */
export function RigFracNode({ node }: IRigFracNode) {
  const { selectedProjectId } = useProjectContext();
  const { hasFeature } = useBetaFeatures();
  const setSelectedRigFracLayer = useMapStore((s) => s.setSelectedRigFracLayer);

  const layerList = useProjectLayerListQuery({ projectId: selectedProjectId });

  const layerId = useMemo(
    () =>
      layerList.data?.find(
        (l) => l.projectLayerType === RIG_FRAC_LAYER_TYPE && l.name === node.title
      )?.layerId,
    [layerList.data, node.title]
  );

  return (
    <NodeTitle>
      <FlexItem>
        <label>{node.title}</label>
      </FlexItem>
      <FlexItem>
        <LayerActionsWrapper>
          <MapLayerSettings
            layerName={node.key}
            zoomSliderProps={{
              layerName: node.key,
              isShapefileLayer: false
            }}
          />
          {hasFeature("Rig And Frac Layers") && (
            <>
              {/* Filter is keyed by layer name only — reachable even while the
                  layer-list query (which resolves layerId) is in flight. */}
              <RigFracFilterButton layerName={node.title} />
              {layerId && (
                <RigFracDetailsButton
                  viewLayerDetails={() =>
                    setSelectedRigFracLayer({ layerId, name: node.title })
                  }
                />
              )}
            </>
          )}
        </LayerActionsWrapper>
      </FlexItem>
    </NodeTitle>
  );
}
